import { APP_INITIALIZER, FactoryProvider, Injectable, inject } from '@angular/core';

import {
  NEVER,
  Observable,
  ReplaySubject,
  catchError,
  finalize,
  fromEventPattern,
  map,
  of,
  switchMap,
  take,
  tap
} from 'rxjs';

import {
  EnvironmentService,
  HostContext,
  PlatformService,
  TokenService,
  WindowRefService
} from '@hdms/shared/util-core';

import { UserContextService } from '../user';

interface UserDetailsResult {
  userAuthentication: boolean;
  csrfToken: string;
  error: boolean;
}

interface AemExtendedWindow extends Window {
  aemUserDetailsLoader: {
    registerCallback: (cb: (res: UserDetailsResult) => void) => void;
  };
}

@Injectable({
  providedIn: 'root'
})
export class HostContextService {
  public readonly ready$: Observable<never | void>;

  private readonly platform = inject(PlatformService);
  private readonly envService = inject(EnvironmentService);
  private readonly tokenService = inject(TokenService);
  private readonly userContext = inject(UserContextService);
  private readonly win = inject<WindowRefService<AemExtendedWindow>>(WindowRefService).nativeWindow;

  private readonly readySubject = new ReplaySubject<void>(1);

  constructor() {
    this.ready$ = this.getReady$();
  }

  public get context(): HostContext {
    return this.envService.hostContext;
  }

  /**
   * In AEM context block app init unless data via HOL userDetails.json endpoint is loaded.
   * Check will run in browser only, as potentially never resolving would hang SSR.
   */
  public checkUserDetails(): Observable<boolean> | true {
    if (this.platform.isBrowser && this.context === HostContext.AEM) {
      return fromEventPattern<UserDetailsResult>(handler =>
        this.win.aemUserDetailsLoader.registerCallback(handler)
      ).pipe(
        take(1),
        tap(({ userAuthentication, csrfToken, error }) => {
          if (error) {
            throw new Error();
          }
          this.envService.legacyEnv.loggedIn = userAuthentication;
          this.tokenService.csrfToken = csrfToken;
        }),
        map(_ => true),
        catchError(_ => {
          this.userContext.forceErrorState();
          return of(true);
        }),
        finalize(() => {
          this.readySubject.next();
          this.readySubject.complete();
        })
      );
    }

    return true;
  }

  /**
   * Returned observable emits always when in SSR mode or not in hostContext.
   * When in hostContext, it emits never if auth is required, in order to block
   * further request invocation. In AEM hostContext, emitting will be triggered
   * via `readySubject`.
   */
  private getReady$(): Observable<never | void> {
    if (this.envService.ssrCachingEnabled || !this.context) {
      return of(null);
    }

    const neverOrNull = (): Observable<never | void> => (this.userContext.requiresAuth ? NEVER : of(null));

    return this.context === HostContext.AEM ? this.readySubject.pipe(switchMap(() => neverOrNull())) : neverOrNull();
  }
}

export const USER_DETAILS_PROVIDER: FactoryProvider = {
  provide: APP_INITIALIZER,
  useFactory(context: HostContextService) {
    return () => context.checkUserDetails();
  },
  deps: [HostContextService],
  multi: true
};
